<div class="card">
  <h2 class="card-title">Expense claims</h2>

  <div class="card-body">
    <form [formGroup]="filters" class="toolbar" (ngSubmit)="load()">
      <label>
        Status
        <select formControlName="status">
          <option value="">All statuses</option>
          @for (status of statuses; track status) {
            <option [value]="status">{{ status }}</option>
          }
        </select>
      </label>

      <label>
        Scholarship
        <select formControlName="scholarshipId">
          <option [ngValue]="null">All scholarships</option>
          @for (scholarship of scholarships(); track scholarship.id) {
            <option [ngValue]="scholarship.id">{{ scholarship.title }}</option>
          }
        </select>
      </label>

      <label>
        Search
        <input type="text" formControlName="search" placeholder="Reference, student or award" />
      </label>

      <div class="toolbar-actions">
        <button type="submit" class="btn btn-primary btn-sm"><i class="fas fa-search"></i> Filter</button>
      </div>
    </form>

    @if (error()) {
      <div class="alert alert-warning"><i class="fas fa-exclamation-circle"></i> {{ error() }}</div>
    }
    @if (message()) {
      <div class="alert alert-success"><i class="fas fa-check-circle"></i> {{ message() }}</div>
    }

    <!-- Bulk decisions -->
    @if (authorization.hasPermission(permissions.ClaimReview)) {
      <div class="action-panel">
        <strong>{{ chosen().size }} selected</strong>
        <form [formGroup]="bulkForm" class="inline-form" (ngSubmit)="bulkDecide()">
          <select formControlName="decision">
            <option value="Approve">Approve</option>
            <option value="Reject">Reject</option>
          </select>
          @if (bulkForm.get('decision')?.value === 'Reject') {
            <select formControlName="reasonCode">
              @for (code of reasonCodes; track code.value) {
                <option [value]="code.value">{{ code.label }}</option>
              }
            </select>
            <input type="text" formControlName="reason" placeholder="Reason (sent to the student)" />
          }
          <button type="submit" class="btn btn-primary btn-sm" [disabled]="busy() || chosen().size === 0">
            Apply to selected
          </button>
        </form>
        @if (bulkResult(); as result) {
          <div class="result-note">
            {{ result.succeeded }} updated, {{ result.skipped }} skipped.
            @for (skip of result.skips; track skip.id) {
              <small>#{{ skip.id }}: {{ skip.reason }}</small>
            }
          </div>
        }
      </div>
    }

    <div class="table-responsive">
      <table class="table">
        <thead>
          <tr>
            <th class="select-column">
              <input type="checkbox" [checked]="chosen().size > 0 && chosen().size === claims().length"
                (change)="toggleAll()" aria-label="Select all claims" />
            </th>
            <th>Reference</th>
            <th>Student</th>
            <th>Scholarship</th>
            <th>Category</th>
            <th>Amount</th>
            <th>Submitted</th>
            <th>Status</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          @if (loading()) {
            <tr><td colspan="9" class="empty-cell"><i class="fas fa-spinner fa-spin"></i> Loading claims…</td></tr>
          }
          @if (!loading() && claims().length === 0) {
            <tr><td colspan="9" class="empty-cell">No claims match these filters.</td></tr>
          }
          @for (claim of claims(); track claim.id) {
            <tr>
              <td class="select-column">
                <input type="checkbox" [checked]="isChosen(claim.id)" (change)="toggle(claim.id)"
                  [attr.aria-label]="'Select claim ' + claim.referenceNumber" />
              </td>
              <td>{{ claim.referenceNumber }}</td>
              <td>
                {{ claim.student.fullName || '—' }}
                @if (claim.awardReferenceNumber) { <small>{{ claim.awardReferenceNumber }}</small> }
              </td>
              <td>{{ claim.scholarshipTitle || '—' }}</td>
              <td>{{ claim.benefitName }}</td>
              <td>{{ claim.amount | number: '1.2-2' }} {{ claim.currency }}</td>
              <td>{{ claim.submittedDate | date: 'mediumDate' }}</td>
              <td>
                <span [class]="statusClass(claim.status)">{{ claim.status }}</span>
                @if (claim.status === 'REJECTED' && claim.decisionReason) {
                  <small>{{ claim.decisionReason }}</small>
                }
              </td>
              <td>
                <button type="button" class="btn btn-secondary btn-sm" (click)="open(claim)">Review</button>
              </td>
            </tr>
          }
        </tbody>
      </table>
    </div>
  </div>
</div>


<!-- Claim review --------------------------------------------------------- -->
@if (selected(); as claim) {
  <div class="modal-backdrop" (click)="close()">
    <div class="modal-dialog" (click)="$event.stopPropagation()">
      <div class="modal-header">
        <h2>Claim {{ claim.referenceNumber }}</h2>
        <button class="modal-close" (click)="close()"><i class="fas fa-times"></i></button>
      </div>

      <div class="modal-body">
        <div class="detail-grid">
          <div class="detail-item"><span class="detail-label">Status</span>
            <span class="detail-value"><span [class]="statusClass(claim.status)">{{ claim.status }}</span></span>
          </div>
          <div class="detail-item"><span class="detail-label">Student</span><span class="detail-value">{{ claim.student.fullName || '—' }}</span></div>
          <div class="detail-item"><span class="detail-label">Email</span><span class="detail-value">{{ claim.student.emailAddress || '—' }}</span></div>
          <div class="detail-item"><span class="detail-label">Scholarship</span><span class="detail-value">{{ claim.scholarshipTitle || '—' }}</span></div>
          <div class="detail-item"><span class="detail-label">Award</span><span class="detail-value">{{ claim.awardReferenceNumber || '—' }}</span></div>
          <div class="detail-item"><span class="detail-label">Category</span><span class="detail-value">{{ claim.benefitName }}</span></div>
          <div class="detail-item"><span class="detail-label">Amount</span>
            <span class="detail-value">{{ claim.amount | number: '1.2-2' }} {{ claim.currency }}</span>
          </div>
          <div class="detail-item"><span class="detail-label">Date incurred</span><span class="detail-value">{{ claim.dateIncurred | date: 'mediumDate' }}</span></div>
          <div class="detail-item"><span class="detail-label">Submitted</span><span class="detail-value">{{ claim.submittedDate | date: 'medium' }}</span></div>
          @if (claim.resubmissionCount > 0) {
            <div class="detail-item"><span class="detail-label">Resubmissions</span><span class="detail-value">{{ claim.resubmissionCount }}</span></div>
          }
          @if (claim.paidAmount) {
            <div class="detail-item"><span class="detail-label">Paid</span>
              <span class="detail-value">{{ claim.paidAmount | number: '1.2-2' }} {{ claim.currency }}
                @if (claim.paymentReference) { ({{ claim.paymentReference }}) }
                @if (claim.paidDate) { · {{ claim.paidDate | date: 'mediumDate' }} }
              </span>
            </div>
          }
        </div>

        @if (claim.description) {
          <p class="detail-note">{{ claim.description }}</p>
        }

        @if (claim.status === 'REJECTED' && claim.decisionReason) {
          <div class="alert alert-warning">
            <i class="fas fa-exclamation-circle"></i>
            <strong>Rejected:</strong> {{ claim.decisionReason }}
            @if (claim.decisionReasonCode) { <small>({{ claim.decisionReasonCode }})</small> }
          </div>
        }

        <!-- Supporting documents -->
        <h3 class="section-heading">Supporting documents</h3>
        <ul class="document-list">
          @if (claim.documents.length === 0) {
            <li>No documents attached.</li>
          }
          @for (document of claim.documents; track document.id) {
            <li>
              <i class="fas fa-file"></i> {{ document.fileName }}
              @if (document.isResubmission) { <span class="status status-booked">Resubmission</span> }
              <span class="status status-pending">{{ document.reviewStatus }}</span>
              <button type="button" class="inline-link" (click)="download(claim, document.id, document.fileName)">Download</button>
              @if (authorization.hasPermission(permissions.ClaimReview)) {
                <button type="button" class="inline-link" (click)="reviewDocument(document.id, 'Approved')">Verify</button>
                <button type="button" class="inline-link" (click)="reviewDocument(document.id, 'Rejected')">Reject</button>
              }
            </li>
          }
        </ul>

        <!-- Decision -->
        @if (canDecide(claim) && authorization.hasPermission(permissions.ClaimReview)) {
          <h3 class="section-heading">Decision</h3>
          <form [formGroup]="decisionForm" class="form-grid">
            <label>
              Rejection reason (required to reject)
              <select formControlName="reasonCode">
                @for (code of reasonCodes; track code.value) {
                  <option [value]="code.value">{{ code.label }}</option>
                }
              </select>
            </label>
            <label>
              Reason sent to the student
              <input type="text" formControlName="reason" placeholder="e.g. Upload a clearer receipt" />
            </label>
          </form>
        }

        <!-- Settlement -->
        @if (claim.status === 'APPROVED' && authorization.hasPermission(permissions.ClaimPay)) {
          <h3 class="section-heading">Record payment</h3>
          <form [formGroup]="paidForm" class="form-grid">
            <label>
              Paid amount
              <input type="number" step="0.01" formControlName="paidAmount" />
            </label>
            <label>
              Payment reference
              <input type="text" formControlName="paymentReference" />
            </label>
            <label>
              Paid date
              <input type="date" formControlName="paidDate" />
            </label>
            <label>
              Notes
              <input type="text" formControlName="notes" />
            </label>
          </form>
        }
      </div>

      <div class="modal-footer">
        <button type="button" class="btn btn-secondary" (click)="close()">Close</button>

        @if (canDecide(claim) && authorization.hasPermission(permissions.ClaimReview)) {
          <button type="button" class="btn btn-secondary" (click)="decide('Reject')" [disabled]="busy()">Reject</button>
          <button type="button" class="btn btn-primary" (click)="decide('Approve')" [disabled]="busy()">Approve</button>
        }

        @if (claim.status === 'APPROVED' && authorization.hasPermission(permissions.ClaimPay)) {
          <button type="button" class="btn btn-primary" (click)="markPaid()" [disabled]="busy()">
            {{ busy() ? 'Saving…' : 'Mark as paid' }}
          </button>
        }
      </div>
    </div>
  </div>
}
